import { lazy, Suspense, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../auth/authContext.tsx";
import { getDbFeatureFlags } from "../../util/dbFeatureFlags.ts";
import { Button } from "../../components/ui/button.tsx";
import { StatusIcon } from "../../components/ui/statusIcon.tsx";
import {
  PackageDealMode,
  PackageDealType,
  PackageDealTimeType,
  parseTypeArgs,
  getPackageDeal,
  type PackageDeal,
  type PackageDealGroup,
  type PackageDealTime,
  type PackageDealTypeArgs,
  type PackageDealLocationAccess,
} from "../../api/packageDeal.ts";
import { getKioskInventoryPlanogram, type Planogram } from "../../api/planogram.ts";
import { getPagePath } from "../pageRegistry.ts";
import type { IssaChatActionRaw } from "./issaResponseHelpers.ts";
import type { ProductPriceChange } from "./issaPriceChange.ts";
import type { PreferredStockChange } from "./preferredStockModal.tsx";
import { useAssistant } from "../../config/assistantProfile.ts";
import { actionKey, getRecordedAction, recordAction, type RecordedDealAction } from "./issaActionState.ts";
import styles from "./issaInsights.module.scss";

/**
 * Actionable cards emitted by Issa inside <button>{json}</button> tags.
 *
 * Four actions:
 *  - `createPackageDeal` — without `dealId` it creates a new deal; with `dealId` it
 *    updates that deal, applying ONLY the fields Issa supplied on top of the stored
 *    one (so a partial suggestion can't silently wipe groups or locations).
 *  - `editProduct` — one product opens the product editor with Issa's values filled
 *    in; several products open a price review list. Name / external item ID /
 *    external cost are single-product only.
 *  - `setPreferredStockLevel` — restock targets for one kiosk, applied through the
 *    Stocking grid after a before/after review.
 *  - `createPlanogram` — a new slot layout for one kiosk: the kiosk's live layout is
 *    fetched, Issa's slot changes are applied by slot name, and the planogram editor
 *    opens in create mode with those rows marked.
 *
 * None of them writes anything by itself — each opens the ordinary editor and the
 * operator saves. What the buttons did is remembered client-side (see
 * issaActionState.ts), so re-opening the conversation shows "Created deal #123"
 * with a way to open it rather than offering to create a duplicate.
 */

// Lazy so the deal editor doesn't ride into every chunk that renders chat
// messages (the cockpit dashboard embeds the quick chat).
const PackageDealEditor = lazy(() =>
  import("../packageDeal/packageDealEditor.tsx").then((m) => ({ default: m.PackageDealEditor })),
);
const ProductPopup = lazy(() =>
  import("../product/searchEditProductPage.tsx").then((m) => ({ default: m.ProductPopup })),
);
const ProductPriceChangeModal = lazy(() =>
  import("./productPriceChangeModal.tsx").then((m) => ({ default: m.ProductPriceChangeModal })),
);
const PreferredStockModal = lazy(() =>
  import("./preferredStockModal.tsx").then((m) => ({ default: m.PreferredStockModal })),
);
const EditPlanogramDialog = lazy(() =>
  import("../planogram/editPlanogramDialog.tsx").then((m) => ({ default: m.EditPlanogramDialog })),
);

/* ── Issa JSON → PackageDeal mapping ─────────────────────── */

interface CreatePackageDealBody {
  /** When set (> 0) this action updates that deal instead of creating a new one. */
  dealId?: number;
  title?: string;
  /** Short human-readable summary of the deal, shown on the chat card. */
  description?: string;
  startTime?: string;
  endTime?: string;
  dealType?: string;
  dealValue?: number;
  atLeast?: number;
  upTo?: number;
  minAmount?: number;
  maxAmount?: number;
  minCartAmount?: number;
  maxCartAmount?: number;
  validLocations?: { locations?: number[]; locationGroups?: number[] };
  /** `itemCount` = units the cart needs from the group (group `i`, default 1); `freeMix` =
   *  those units may be any mix of the group's products (group `m`) instead of one product.
   *  Either may be omitted on an edit: a same-named stored group then keeps its own. */
  products?: { groups?: { groupName?: string; products?: number[]; itemCount?: number; freeMix?: boolean }[] };
  validTimes?: { day?: string; startTime?: string; endTime?: string }[];
  /** Attach a picture generated in the same answer as the deal's kiosk image:
   *  `true` or `1` for the answer's (first) picture, otherwise its 1-based
   *  position. The frontend resolves it against the message's images. */
  attachImage?: number | boolean;
}

const DEAL_TYPE_MAP: Record<string, PackageDealType> = {
  percent: PackageDealType.Percent,
  flatreduction: PackageDealType.FlatReduction,
  flatprice: PackageDealType.FlatPrice,
};

const DAY_MAP: Record<string, PackageDealTimeType> = {
  sunday: PackageDealTimeType.Sunday,
  sundays: PackageDealTimeType.Sunday,
  monday: PackageDealTimeType.Monday,
  mondays: PackageDealTimeType.Monday,
  tuesday: PackageDealTimeType.Tuesday,
  tuesdays: PackageDealTimeType.Tuesday,
  wednesday: PackageDealTimeType.Wednesday,
  wednesdays: PackageDealTimeType.Wednesday,
  thursday: PackageDealTimeType.Thursday,
  thursdays: PackageDealTimeType.Thursday,
  friday: PackageDealTimeType.Friday,
  fridays: PackageDealTimeType.Friday,
  saturday: PackageDealTimeType.Saturday,
  saturdays: PackageDealTimeType.Saturday,
  weekdays: PackageDealTimeType.Weekdays,
  weekends: PackageDealTimeType.Weekends,
  any: PackageDealTimeType.Any,
};

/** Only the deal fields Issa actually supplied — the update path applies this on
 *  top of the stored deal so unmentioned fields keep their current values. */
interface DealPatch {
  fields: Partial<Omit<PackageDeal, "typeArgs">>;
  typeArgs: Partial<PackageDealTypeArgs>;
}

type MappedDeal =
  | {
      ok: true;
      /** Full deal for the create path (patch applied over blank defaults). */
      deal: PackageDeal;
      /** Sparse patch for the update path. */
      patch: DealPatch;
      /** Target deal id — 0 means "create new". */
      dealId: number;
      /** Presentational only — the PackageDeal model has no such field. */
      description: string;
      /** 1-based position of this answer's generated image to attach as the
       *  deal's kiosk image, or null when none was requested. */
      imageIndex: number | null;
    }
  | { ok: false; error: string };

function num(v: unknown): number | null {
  return typeof v === "number" && Number.isFinite(v) ? v : null;
}

function idsToRecord(ids: unknown): Record<number, boolean> {
  const out: Record<number, boolean> = {};
  if (Array.isArray(ids)) {
    for (const id of ids) {
      const n = Number(id);
      if (Number.isFinite(n) && n > 0) out[n] = true;
    }
  }
  return out;
}

export function mapCreatePackageDeal(bodyRaw: unknown): MappedDeal {
  const body = bodyRaw as CreatePackageDealBody | null;
  if (!body || typeof body !== "object") return { ok: false, error: "missing deal body" };

  const dealId = Math.max(0, num(body.dealId) ?? 0);
  const isUpdate = dealId > 0;

  const title = (body.title ?? "").trim();
  // A new deal needs a title; an update may legitimately leave it unchanged.
  if (!title && !isUpdate) return { ok: false, error: "the deal has no title" };

  const rawType = (body.dealType ?? "").trim().toLowerCase();
  const type = rawType ? DEAL_TYPE_MAP[rawType] : undefined;
  if (rawType && type === undefined) return { ok: false, error: `unknown deal type "${body.dealType ?? ""}"` };
  if (!rawType && !isUpdate) return { ok: false, error: "the deal has no deal type" };

  const rawValue = num(body.dealValue);
  if (rawValue != null && rawValue < 0) return { ok: false, error: "the deal value is negative" };
  const value =
    rawValue == null ? null : type === PackageDealType.Percent ? Math.min(100, Math.max(0, rawValue)) : rawValue;

  // Product groups — supplying `products` replaces the group list wholesale
  // (that is the only sensible reading of "these are the valid products").
  // An omitted itemCount stays null and an omitted freeMix stays absent, so the update
  // path can tell "not given" apart and keep the stored group's own (applyDealPatch).
  let groups: PackageDealGroup[] | undefined;
  if (body.products?.groups) {
    groups = [];
    for (const g of body.products.groups) {
      const p = idsToRecord(g?.products);
      if (Object.keys(p).length === 0) {
        return { ok: false, error: `product group "${g?.groupName ?? ""}" has no products` };
      }
      const n = (g.groupName ?? "").trim() || `Group ${groups.length + 1}`;
      // Rejected rather than dropped: a lost itemCount of 2 would turn "any 2" into "any 1",
      // a deeper discount than Issa proposed.
      const itemCount = num(g.itemCount);
      if (g.itemCount != null && (itemCount == null || !Number.isInteger(itemCount) || itemCount < 1)) {
        return { ok: false, error: `product group "${n}" has an invalid itemCount` };
      }
      if (g.freeMix != null && typeof g.freeMix !== "boolean") {
        return { ok: false, error: `product group "${n}" has an invalid freeMix` };
      }
      groups.push({
        i: itemCount,
        n,
        p,
        t: {},
        l: {},
        s: null,
        ...(g.freeMix != null ? { m: g.freeMix } : {}),
      });
    }
  }

  let validDays: PackageDealTime[] | undefined;
  if (body.validTimes) {
    validDays = [];
    for (const t of body.validTimes) {
      const d = DAY_MAP[(t?.day ?? "").trim().toLowerCase()];
      if (d === undefined) return { ok: false, error: `unknown day "${t?.day ?? ""}" in validTimes` };
      validDays.push({ d, s: t.startTime || "00:00:00", e: t.endTime || "23:59:59" });
    }
  }

  let locationAccess: PackageDealLocationAccess | null | undefined;
  if (body.validLocations) {
    const locations = idsToRecord(body.validLocations.locations);
    const lists = idsToRecord(body.validLocations.locationGroups);
    locationAccess =
      Object.keys(locations).length > 0 || Object.keys(lists).length > 0
        ? {
            locations: Object.keys(locations).length > 0 ? locations : null,
            lists: Object.keys(lists).length > 0 ? lists : null,
          }
        : null;
  }

  // Sparse patch — only keys Issa supplied.
  const fields: Partial<Omit<PackageDeal, "typeArgs">> = {};
  if (title) fields.title = title;
  if (type !== undefined) fields.type = type;
  if (body.startTime !== undefined) fields.validStart = body.startTime?.trim() || null;
  if (body.endTime !== undefined) fields.validEnd = body.endTime?.trim() || null;
  if (groups !== undefined) fields.groups = groups;
  if (validDays !== undefined) fields.validDays = validDays;
  if (locationAccess !== undefined) fields.locationAccess = locationAccess;

  const typeArgsPatch: Partial<PackageDealTypeArgs> = {};
  if (value != null) typeArgsPatch.value = value;
  if (type === PackageDealType.Percent) {
    if (num(body.atLeast) != null) typeArgsPatch.minReduction = num(body.atLeast);
    if (num(body.upTo) != null) typeArgsPatch.maxReduction = num(body.upTo);
  }
  if (num(body.minAmount) != null) typeArgsPatch.minAmount = num(body.minAmount);
  if (num(body.maxAmount) != null) typeArgsPatch.maxAmount = num(body.maxAmount);
  if (num(body.minCartAmount) != null) typeArgsPatch.minCart = num(body.minCartAmount);
  if (num(body.maxCartAmount) != null) typeArgsPatch.maxCart = num(body.maxCartAmount);

  const patch: DealPatch = { fields, typeArgs: typeArgsPatch };

  // Create path: patch over a blank Standard deal. Standard deals must carry all
  // four amount bounds, so unmentioned ones fall back to "allow any" (0 / 9999).
  const deal: PackageDeal = {
    id: 0,
    title,
    imagePath: "",
    validStart: null,
    validEnd: null,
    bias: 0,
    validDays: [],
    type: type ?? PackageDealType.FlatPrice,
    typeArgs: {
      value: 0,
      minReduction: null,
      maxReduction: null,
      minAmount: 0,
      maxAmount: 9999,
      minCart: 0,
      maxCart: 9999,
      ...typeArgsPatch,
    },
    groups: [],
    membership: null,
    operatorId: null,
    locationAccess: null,
    externalReference: "",
    appIconUrl: "",
    ...fields,
  };
  // A group Issa sent without an itemCount needs one product on a new deal, as the editor's
  // "Add group" starts it. The patch keeps it null, so an edit keeps the stored count.
  deal.groups = deal.groups.map((g) => (g.i == null ? { ...g, i: defaultItemCount(deal.type) } : g));

  const attach = body.attachImage;
  const imageIndex =
    attach === true ? 1
    : typeof attach === "number" && Number.isFinite(attach) && attach >= 1 ? Math.floor(attach)
    : null;

  return { ok: true, deal, patch, dealId, description: (body.description ?? "").trim(), imageIndex };
}

/** The item count a new group starts with: one product, as the editor's "Add group" makes
 *  it. Subsidy and interactive groups count items differently and start unset. */
function defaultItemCount(type: PackageDealType): number | null {
  return type === PackageDealType.Subsidy || type === PackageDealType.Interactive ? null : 1;
}

/** Apply a sparse patch onto a stored deal, merging typeArgs field-by-field. */
export function applyDealPatch(stored: PackageDeal, patch: DealPatch): PackageDeal {
  const typeArgs: PackageDealTypeArgs = { ...parseTypeArgs(stored.typeArgs), ...patch.typeArgs };
  // Clamp against the EFFECTIVE type: an update that changes only the value carries no
  // dealType, so the map-time clamp (which needs a supplied type) doesn't apply here.
  const type = patch.fields.type ?? stored.type;
  if (type === PackageDealType.Percent && typeof typeArgs.value === "number") {
    typeArgs.value = Math.min(100, Math.max(0, typeArgs.value));
  }
  const fields = patch.fields.groups
    ? { ...patch.fields, groups: carryOverGroupSettings(stored.groups, patch.fields.groups, type) }
    : patch.fields;
  return { ...stored, ...fields, typeArgs };
}

/**
 * Issa rebuilds a group from its name and products; the item count, split part and free-mix
 * flag it only sends when it means to change them (split never). Where Issa left one out,
 * take it from the stored group of the same name (trimmed, case-insensitive, first match),
 * so swapping the products of "any 2 candies" doesn't quietly make it "one candy".
 */
function carryOverGroupSettings(
  stored: readonly PackageDealGroup[] | undefined,
  groups: readonly PackageDealGroup[],
  type: PackageDealType,
): PackageDealGroup[] {
  const byName = new Map<string, PackageDealGroup>();
  for (const g of Array.isArray(stored) ? stored : []) {
    const k = (g?.n ?? "").trim().toLowerCase();
    if (!byName.has(k)) byName.set(k, g);
  }
  return groups.map((g) => {
    const prev = byName.get(g.n.trim().toLowerCase());
    // A group the deal did not have: nothing to carry, so it starts like a new one.
    if (!prev) return g.i == null ? { ...g, i: defaultItemCount(type) } : g;
    return {
      ...g,
      i: g.i ?? prev.i ?? null,
      s: g.s ?? prev.s ?? null,
      ...(g.m === undefined && prev.m !== undefined ? { m: prev.m } : {}),
    };
  });
}

/**
 * Free mix written the way kiosks without it understand: a free-mix group of N becomes N
 * identical groups of one ("Candy 1", "Candy 2", …), which prices exactly the same on a kiosk
 * that has the flag and correctly on one that does not. Used while the DB's
 * `PackageDealFreeMix` flag is off, so an Issa suggestion never saves an "m" the fleet may
 * not read yet. Every other group comes back unchanged, minus an "m" that could mean nothing.
 */
export function expandFreeMixGroups(groups: readonly PackageDealGroup[]): PackageDealGroup[] {
  const out: PackageDealGroup[] = [];
  for (const g of groups) {
    const count = g.i ?? 1;
    if (!g.m && !("m" in g)) {
      out.push(g);
      continue;
    }
    const { m, ...rest } = g;
    if (!m || count <= 1) {
      out.push(rest);
      continue;
    }
    for (let k = 1; k <= count; k++) out.push({ ...rest, n: `${g.n} ${k}`, i: 1 });
  }
  return out;
}

/**
 * Card wording for what the cart must hold: "any 2 from Candy", "2 of one product from
 * Candy + 1 from Drink". Null when every group needs a single item, where the badge and
 * title already say it all. A null `i` counts as 1, the create default.
 */
export function describeDealGroups(groups: readonly PackageDealGroup[] | undefined): string | null {
  const list = groups ?? [];
  if (!list.some((g) => (g.i ?? 1) > 1)) return null;
  return list
    .map((g) => {
      const count = g.i ?? 1;
      if (count <= 1) return `1 from ${g.n}`;
      return g.m ? `any ${count} from ${g.n}` : `${count} of one product from ${g.n}`;
    })
    .join(" + ");
}

/** Field tokens the patch touches — handed to the editor so it can highlight them.
 *  The patch keys are already `PackageDeal` / `typeArgs` key names. */
export function patchHighlightFields(patch: DealPatch): string[] {
  return [...Object.keys(patch.fields), ...Object.keys(patch.typeArgs)];
}

/* ── editProduct mapping ──────────────────────────────────── */

interface EditProductEntry {
  productId?: number;
  productName?: string;
  price?: number;
  tax?: number;
  newProductName?: string;
  externalItemId?: string;
  externalCost?: number;
}

interface EditProductBody {
  description?: string;
  products?: EditProductEntry[];
}

type MappedProductEdit =
  | { ok: true; changes: ProductPriceChange[]; description: string }
  | { ok: false; error: string };

/** Fields that only make sense for a single product — the bulk review list is a
 *  price/tax table, so a rename or an external-field change needs one product. */
const SINGLE_ONLY_FIELDS: { key: keyof EditProductEntry; label: string }[] = [
  { key: "newProductName", label: "a new product name" },
  { key: "externalItemId", label: "an external item ID" },
  { key: "externalCost", label: "an external cost" },
];

export function mapEditProduct(bodyRaw: unknown): MappedProductEdit {
  const body = bodyRaw as EditProductBody | null;
  if (!body || typeof body !== "object") return { ok: false, error: "missing product-edit body" };

  const list = body.products;
  if (!Array.isArray(list) || list.length === 0) {
    return { ok: false, error: "no products were given" };
  }

  const changes: ProductPriceChange[] = [];
  const seen = new Set<number>();
  for (const p of list) {
    const id = num(p?.productId);
    if (id == null || id <= 0) return { ok: false, error: "a product is missing a valid productId" };
    if (seen.has(id)) return { ok: false, error: `product #${id} is listed more than once` };
    seen.add(id);

    const price = num(p.price);
    const tax = num(p.tax);
    if ((price != null && price < 0) || (tax != null && tax < 0)) {
      return { ok: false, error: `product #${id} has a negative price or tax` };
    }

    const newName = (p.newProductName ?? "").trim() || undefined;
    const externalItemId = p.externalItemId != null ? String(p.externalItemId).trim() : undefined;
    const externalCost = num(p.externalCost) ?? undefined;
    if (externalCost != null && externalCost < 0) {
      return { ok: false, error: `product #${id} has a negative external cost` };
    }

    if (price == null && tax == null && newName == null && externalItemId == null && externalCost == null) {
      return { ok: false, error: `product #${id} has no changes` };
    }

    // Guard the single-product-only fields before building the change.
    if (list.length > 1) {
      const offender = SINGLE_ONLY_FIELDS.find(({ key }) => {
        const v = p[key];
        return v != null && String(v).trim() !== "";
      });
      if (offender) {
        return {
          ok: false,
          error: `${offender.label} can only be changed for one product at a time (${list.length} were given)`,
        };
      }
    }

    changes.push({
      productId: id,
      productName: (p.productName ?? "").trim(),
      price,
      tax,
      ...(newName != null ? { newName } : {}),
      ...(externalItemId ? { externalItemId } : {}),
      ...(externalCost != null ? { externalCost } : {}),
    });
  }

  return { ok: true, changes, description: (body.description ?? "").trim() };
}

/* ── setPreferredStockLevel mapping ───────────────────────── */

interface PreferredStockBody {
  kioskId?: number;
  description?: string;
  products?: { productId?: number; productName?: string; preferredStockLevel?: number }[];
}

type MappedPreferredStock =
  | { ok: true; kioskId: number; changes: PreferredStockChange[]; description: string }
  | { ok: false; error: string };

export function mapSetPreferredStockLevel(bodyRaw: unknown): MappedPreferredStock {
  const body = bodyRaw as PreferredStockBody | null;
  if (!body || typeof body !== "object") return { ok: false, error: "missing stock-level body" };

  const kioskId = num(body.kioskId) ?? 0;
  if (kioskId <= 0) return { ok: false, error: "no kiosk was given" };

  const list = body.products;
  if (!Array.isArray(list) || list.length === 0) return { ok: false, error: "no products were given" };

  const changes: PreferredStockChange[] = [];
  const seen = new Set<number>();
  for (const p of list) {
    const id = num(p?.productId);
    if (id == null || id <= 0) return { ok: false, error: "a product is missing a valid productId" };
    if (seen.has(id)) return { ok: false, error: `product #${id} is listed more than once` };
    seen.add(id);

    const level = num(p.preferredStockLevel);
    if (level == null) return { ok: false, error: `product #${id} has no preferredStockLevel` };
    if (level < 0) return { ok: false, error: `product #${id} has a negative preferredStockLevel` };
    if (!Number.isInteger(level)) return { ok: false, error: `product #${id} has a fractional preferredStockLevel` };

    changes.push({ productId: id, productName: (p.productName ?? "").trim(), level });
  }

  return { ok: true, kioskId, changes, description: (body.description ?? "").trim() };
}

/* ── createPlanogram mapping ──────────────────────────────── */

interface CreatePlanogramBody {
  kioskId?: number;
  name?: string;
  description?: string;
  slots?: { slotName?: string; productId?: number; productName?: string; maxProductCount?: number }[];
}

/** One slot Issa wants changed in the kiosk's current layout. */
export interface PlanogramSlotChange {
  /** The kiosk's slot name (`CurrentInventory.SlotName` = `PlanogramSlot.Name`). */
  slotName: string;
  /** Product to place in the slot; 0 leaves the slot empty. */
  productId: number;
  /** Name Issa supplied for the review table; the cache name wins when available. */
  productName: string;
  /** New capacity, or null to keep the slot's current max count. */
  maxProductCount: number | null;
}

type MappedPlanogram =
  | { ok: true; kioskId: number; name: string; description: string; changes: PlanogramSlotChange[] }
  | { ok: false; error: string };

/**
 * Validate a `createPlanogram` body. `slots` lists only the slots that change; an
 * absent or empty list means "save the kiosk's current layout as-is under this name".
 */
export function mapCreatePlanogram(bodyRaw: unknown): MappedPlanogram {
  const body = bodyRaw as CreatePlanogramBody | null;
  if (!body || typeof body !== "object") return { ok: false, error: "missing planogram body" };

  const kioskId = num(body.kioskId) ?? 0;
  if (kioskId <= 0) return { ok: false, error: "no kiosk was given" };

  const name = typeof body.name === "string" ? body.name.trim() : "";
  if (!name) return { ok: false, error: "the planogram has no name" };

  const list = body.slots ?? [];
  if (!Array.isArray(list)) return { ok: false, error: "slots is not a list" };

  const changes: PlanogramSlotChange[] = [];
  const seen = new Set<string>();
  for (const s of list) {
    const slotName = typeof s?.slotName === "string" ? s.slotName.trim() : "";
    if (!slotName) return { ok: false, error: "a slot is missing its slotName" };
    const slotKey = slotName.toLowerCase();
    if (seen.has(slotKey)) return { ok: false, error: `slot ${slotName} is listed more than once` };
    seen.add(slotKey);

    const productId = num(s.productId);
    if (productId == null) return { ok: false, error: `slot ${slotName} has no productId` };
    if (productId < 0 || !Number.isInteger(productId)) {
      return { ok: false, error: `slot ${slotName} has an invalid productId` };
    }

    let maxProductCount: number | null = null;
    if (s.maxProductCount !== undefined && s.maxProductCount !== null) {
      const max = num(s.maxProductCount);
      if (max == null || !Number.isInteger(max) || max < 1) {
        return { ok: false, error: `slot ${slotName} has an invalid maxProductCount` };
      }
      maxProductCount = max;
    }

    changes.push({ slotName, productId, productName: (s.productName ?? "").trim(), maxProductCount });
  }

  return { ok: true, kioskId, name, description: (body.description ?? "").trim(), changes };
}

/**
 * Apply Issa's slot changes to the kiosk's live layout, matching by slot name
 * (case-insensitive, trimmed). Slots Issa did not mention are untouched; slot names
 * the kiosk does not have are reported in `missing` rather than invented, because a
 * slot the kiosk cannot match on shelf + sensor would never be applied. Returns a
 * fresh planogram in create shape (`ID`/`KioskID` = -1); `base` is not mutated.
 */
export function applyPlanogramSlotChanges(
  base: Planogram,
  changes: readonly PlanogramSlotChange[],
): { planogram: Planogram; changed: string[]; missing: PlanogramSlotChange[] } {
  const byName = new Map<string, PlanogramSlotChange>();
  for (const c of changes) byName.set(c.slotName.trim().toLowerCase(), c);

  const changed: string[] = [];
  const matched = new Set<string>();
  const slots = base.ConfigDataSerialized.map((slot) => {
    const key = (slot.Name ?? "").trim().toLowerCase();
    const change = byName.get(key);
    if (!change) return { ...slot };
    matched.add(key);
    changed.push(slot.Name);
    return {
      ...slot,
      ProductID: change.productId,
      MaxProductCount: change.maxProductCount ?? slot.MaxProductCount,
    };
  });
  const missing = changes.filter((c) => !matched.has(c.slotName.trim().toLowerCase()));

  return {
    planogram: { ...base, ID: -1, KioskID: -1, IsBase: false, DiffData: [], ConfigDataSerialized: slots },
    changed,
    missing,
  };
}

/* ── Card ────────────────────────────────────────────────── */

/**
 * Resolve the `action` string to a canonical action.
 *
 * The model does drift on this name (it has emitted `changeProductNames`), and the
 * chat log is immutable server-side, so blocks written under an earlier name —
 * notably `changeProductPrices`, this action's original name — keep re-rendering in
 * saved conversations forever. Both cases are handled by matching on the name with
 * case and non-letters stripped, against a list of accepted spellings.
 */
type CanonicalAction = "createPackageDeal" | "editProduct" | "setPreferredStockLevel" | "createPlanogram";

const ACTION_ALIASES: Record<string, CanonicalAction> = {};
for (const name of [
  "createPackageDeal", "createPackageDeals", "packageDeal", "packageDeals",
  "createDeal", "editPackageDeal", "updatePackageDeal", "changePackageDeal",
]) ACTION_ALIASES[name.toLowerCase()] = "createPackageDeal";
for (const name of [
  "editProduct", "editProducts", "updateProduct", "updateProducts",
  "changeProduct", "changeProducts",
  // Prices — includes `changeProductPrices`, the original name of this action.
  "changeProductPrice", "changeProductPrices", "editProductPrice", "editProductPrices",
  "updateProductPrice", "updateProductPrices", "setProductPrice", "setProductPrices",
  // Names / external fields.
  "changeProductName", "changeProductNames", "renameProduct", "renameProducts",
  "editProductName", "editProductNames", "updateProductName", "updateProductNames",
]) ACTION_ALIASES[name.toLowerCase()] = "editProduct";
for (const name of [
  "setPreferredStockLevel", "setPreferredStockLevels", "setPreferedStockLevel", "setPreferedStockLevels",
  "changePreferredStockLevel", "changePreferredStockLevels", "updatePreferredStockLevel",
  "updatePreferredStockLevels", "editPreferredStockLevel", "editPreferredStockLevels",
  "setStockLevel", "setStockLevels", "changeStockLevel", "changeStockLevels",
]) ACTION_ALIASES[name.toLowerCase()] = "setPreferredStockLevel";
for (const name of [
  "createPlanogram", "createPlanograms", "planogram", "newPlanogram", "suggestPlanogram",
  "proposePlanogram", "buildPlanogram", "generatePlanogram", "makePlanogram",
  "createKioskPlanogram", "planogramFromInventory", "createPlanogramFromInventory",
  "createSlotLayout", "newSlotLayout", "changeSlotLayout",
]) ACTION_ALIASES[name.toLowerCase()] = "createPlanogram";

export function resolveChatAction(raw: string): CanonicalAction | null {
  return ACTION_ALIASES[(raw ?? "").replace(/[^a-zA-Z]/g, "").toLowerCase()] ?? null;
}

/** The note over an action card whose values came from a file the user
 *  attached: the file is untrusted input, so its numbers get a second look. */
export const ACTION_FROM_FILE_NOTE = "Based on your uploaded file — check the values";

export function IssaActionButtons({ fromAttachment, ...props }: {
  action: IssaChatActionRaw;
  /** Chat log row id — used with `index` to remember what this button did. */
  messageId?: number;
  index: number;
  /** Generated images from the same answer (data URIs, in answer order). */
  images?: string[];
  /** The answer read a file the user attached. */
  fromAttachment?: boolean;
}) {
  if (!fromAttachment) return <IssaActionCard {...props} />;
  return (
    <div className={styles.actionFromFile}>
      <div className={styles.actionFromFileNote}>
        <span aria-hidden="true"><StatusIcon kind="warning" size={14} /></span>
        {ACTION_FROM_FILE_NOTE}
      </div>
      <IssaActionCard {...props} />
    </div>
  );
}

function IssaActionCard({ action, messageId, index, images }: {
  action: IssaChatActionRaw;
  messageId?: number;
  index: number;
  images?: string[];
}) {
  const assistant = useAssistant();
  const resolved = resolveChatAction(action.action);
  if (resolved === "createPackageDeal") {
    return <PackageDealActionCard action={action} messageId={messageId} index={index} images={images} />;
  }
  if (resolved === "editProduct") {
    return <ProductEditActionCard action={action} messageId={messageId} index={index} />;
  }
  if (resolved === "setPreferredStockLevel") {
    return <PreferredStockActionCard action={action} messageId={messageId} index={index} />;
  }
  if (resolved === "createPlanogram") {
    return <PlanogramActionCard action={action} messageId={messageId} index={index} />;
  }
  // Never fail silently: an unrecognised action used to render nothing at all, which
  // looks like the answer simply lost its button.
  console.warn(`[IssaActionButtons] Unknown chat action "${action.action}"`);
  return (
    <div className={styles.dealCard}>
      <span className={styles.chatActionError}>
        {assistant.name} suggested an action this portal doesn't support ({action.action || "unnamed"}).
      </span>
    </div>
  );
}

/* ── createPlanogram card ─────────────────────────────────── */

/** What the planogram editor is opened with, once the kiosk's layout has loaded. */
type PlanogramEditorTarget = { planogram: Planogram; highlight: string[] } | null;

function PlanogramActionCard({ action, messageId, index }: {
  action: IssaChatActionRaw;
  messageId?: number;
  index: number;
}) {
  const { cache, canWrite } = useAuth();
  const navigate = useNavigate();
  const assistant = useAssistant();
  const mapped = useMemo(() => mapCreatePlanogram(action.body), [action.body]);
  const key = useMemo(() => actionKey(messageId, index, action), [messageId, index, action]);

  const [recorded, setRecorded] = useState<RecordedDealAction | null>(() => getRecordedAction(key));
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  /** Slot names Issa listed that the kiosk does not have — skipped, and said so. */
  const [missing, setMissing] = useState<PlanogramSlotChange[]>([]);
  /** Slots whose shelf/sensor could not be resolved server-side (ShelfID/Sensor -1). The kiosk
   *  matches a pushed planogram on shelf + sensor, so these rows are never applied. */
  const [unmapped, setUnmapped] = useState(0);
  const [editor, setEditor] = useState<PlanogramEditorTarget>(null);

  // Creating a planogram is an editplanogram write, same as the planogram page itself.
  const pageRowId = useMemo(
    () => cache?.PageInfo.Page.find((p) => p.Name?.toLowerCase() === "editplanogram")?.RowID,
    [cache],
  );
  if (pageRowId == null || !canWrite(pageRowId)) return null;

  if (!mapped.ok) {
    return (
      <div className={styles.dealCard}>
        <span className={styles.chatActionError}>
          {assistant.name} suggested a planogram, but it couldn't be read: {mapped.error}. Ask {assistant.name} to suggest it again.
        </span>
      </div>
    );
  }
  const { kioskId, name, description, changes } = mapped;
  const kioskName = cache?.CommonData.Kiosk?.find((k) => k.KioskID === kioskId)?.StoreName;
  const kioskLabel = kioskName ? `${kioskName} (kiosk ${kioskId})` : `Kiosk ${kioskId}`;
  const changeLabel =
    changes.length === 0
      ? "current layout as-is"
      : `${changes.length} ${changes.length === 1 ? "slot change" : "slot changes"}`;

  /**
   * Fetch the kiosk's live layout (its latest inventory snapshot, with shelf/sensor
   * resolved server-side), apply Issa's slot changes by name, and open the editor in
   * create mode. Nothing is saved until the operator presses Create there.
   */
  const handleOpen = async () => {
    setError(null);
    setMissing([]);
    setUnmapped(0);
    setLoading(true);
    try {
      const r = await getKioskInventoryPlanogram(kioskId);
      if (r.bError) throw new Error(r.sMessage || `Could not load the layout of kiosk ${kioskId}. Try again in a moment.`);
      if (!r.oData) {
        throw new Error(`Kiosk ${kioskId} has not reported an inventory snapshot to build a layout from.`);
      }
      const applied = applyPlanogramSlotChanges(r.oData, changes);
      if (changes.length > 0 && applied.changed.length === 0) {
        throw new Error(
          `None of the suggested slots exist on kiosk ${kioskId} (${changes.map((c) => c.slotName).join(", ")}).`,
        );
      }
      setMissing(applied.missing);
      setUnmapped(applied.planogram.ConfigDataSerialized.filter((s) => s.ShelfID < 0 || s.Sensor < 0).length);
      setEditor({ planogram: applied.planogram, highlight: applied.changed });
    } catch (e) {
      setError(e instanceof Error ? e.message : `Could not load the layout of kiosk ${kioskId}. Try again in a moment.`);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className={styles.dealCard}>
      <div className={styles.dealCardHeader}>
        <span className={styles.dealCardBadge}>Planogram</span>
        <span className={styles.dealCardTitle}>
          {name} — {kioskLabel}, {changeLabel}
        </span>
      </div>
      {description && <p className={styles.dealCardDescription}>{description}</p>}

      {recorded && (
        <div className={styles.dealCardDone}>
          <span className={styles.dealCardDoneMark} aria-hidden="true">✓</span>
          Created planogram “{name}”
          {formatRecordedAt(recorded.at) && (
            <span className={styles.dealCardDoneTime}> · {formatRecordedAt(recorded.at)}</span>
          )}
        </div>
      )}

      <div className={styles.chatActionRow}>
        <Button variant="primary" size="sm" loading={loading} onClick={handleOpen}>
          {recorded ? "Create another" : "Review & create"}
        </Button>
        {recorded && (
          <Button variant="secondary" size="sm" onClick={() => navigate(getPagePath("viewplanograms"))}>
            Open planograms
          </Button>
        )}
      </div>

      {error && <span className={styles.chatActionError} role="alert">{error}</span>}
      {missing.length > 0 && (
        <span className={styles.chatActionError}>
          Skipped {missing.length === 1 ? "a slot" : `${missing.length} slots`} kiosk {kioskId} doesn't have:{" "}
          {missing.map((m) => m.slotName).join(", ")}.
        </span>
      )}
      {unmapped > 0 && (
        <p className={styles.dealCardDescription}>
          {unmapped === 1 ? "1 slot has" : `${unmapped} slots have`} no shelf/sensor position (shown as -1 in the
          editor). The kiosk skips such slots when the planogram is pushed.
        </p>
      )}

      {editor && (
        <Suspense fallback={null}>
          <EditPlanogramDialog
            open
            planogramId={-1}
            initialPlanogram={editor.planogram}
            initialName={name}
            highlightSlotNames={editor.highlight}
            onClose={() => setEditor(null)}
            onSaved={() => setRecorded(recordAction(key, kioskId, "planogram", editor.highlight.length))}
          />
        </Suspense>
      )}
    </div>
  );
}

/* ── setPreferredStockLevel card ──────────────────────────── */

function PreferredStockActionCard({ action, messageId, index }: {
  action: IssaChatActionRaw;
  messageId?: number;
  index: number;
}) {
  const { cache, canWrite } = useAuth();
  const assistant = useAssistant();
  const mapped = useMemo(() => mapSetPreferredStockLevel(action.body), [action.body]);
  const key = useMemo(() => actionKey(messageId, index, action), [messageId, index, action]);

  const [recorded, setRecorded] = useState<RecordedDealAction | null>(() => getRecordedAction(key));
  const [open, setOpen] = useState(false);

  // Preferred stock level is edited on the kiosk Stocking tab's grid.
  const pageRowId = useMemo(
    () => cache?.PageInfo.Page.find((p) => p.Name?.toLowerCase() === "productminedit")?.RowID,
    [cache],
  );
  if (pageRowId == null || !canWrite(pageRowId)) return null;

  if (!mapped.ok) {
    return (
      <div className={styles.dealCard}>
        <span className={styles.chatActionError}>
          {assistant.name} suggested new stock levels, but they couldn't be read: {mapped.error}. Ask {assistant.name} to suggest them again.
        </span>
      </div>
    );
  }
  const { kioskId, changes, description } = mapped;
  const kioskName = cache?.CommonData.Kiosk?.find((k) => k.KioskID === kioskId)?.StoreName;

  return (
    <div className={styles.dealCard}>
      <div className={styles.dealCardHeader}>
        <span className={styles.dealCardBadge}>Stock levels</span>
        <span className={styles.dealCardTitle}>
          {kioskName ? `${kioskName} (kiosk ${kioskId})` : `Kiosk ${kioskId}`} — {changes.length}{" "}
          {changes.length === 1 ? "product" : "products"}
        </span>
      </div>
      {description && <p className={styles.dealCardDescription}>{description}</p>}

      {recorded && (
        <div className={styles.dealCardDone}>
          <span className={styles.dealCardDoneMark} aria-hidden="true">✓</span>
          Applied to {recorded.count ?? 1} {(recorded.count ?? 1) === 1 ? "product" : "products"}
          {formatRecordedAt(recorded.at) && (
            <span className={styles.dealCardDoneTime}> · {formatRecordedAt(recorded.at)}</span>
          )}
        </div>
      )}

      <div className={styles.chatActionRow}>
        <Button variant="primary" size="sm" onClick={() => setOpen(true)}>
          {recorded ? "Review" : "Review changes"}
        </Button>
      </div>

      {open && (
        <Suspense fallback={null}>
          <PreferredStockModal
            kioskId={kioskId}
            changes={changes}
            onClose={() => setOpen(false)}
            onApplied={(count) => setRecorded(recordAction(key, kioskId, "prices", count))}
          />
        </Suspense>
      )}
    </div>
  );
}

/* ── editProduct card ─────────────────────────────────────── */

function ProductEditActionCard({ action, messageId, index }: {
  action: IssaChatActionRaw;
  messageId?: number;
  index: number;
}) {
  const { cache, canWrite } = useAuth();
  const assistant = useAssistant();
  const mapped = useMemo(() => mapEditProduct(action.body), [action.body]);
  const key = useMemo(() => actionKey(messageId, index, action), [messageId, index, action]);

  const [recorded, setRecorded] = useState<RecordedDealAction | null>(() => getRecordedAction(key));
  const [open, setOpen] = useState(false);

  // Writing prices is an Advanced Price operation; gate on that page's write permission.
  const pageRowId = useMemo(
    () => cache?.PageInfo.Page.find((p) => p.Name?.toLowerCase() === "advancedpriceproductprices")?.RowID,
    [cache],
  );
  if (pageRowId == null || !canWrite(pageRowId)) return null;

  if (!mapped.ok) {
    return (
      <div className={styles.dealCard}>
        <span className={styles.chatActionError}>
          {assistant.name} suggested a product change, but it couldn't be read: {mapped.error}. Ask {assistant.name} to suggest it again.
        </span>
      </div>
    );
  }
  const { changes, description } = mapped;
  const single = changes.length === 1 ? changes[0]! : null;
  const productName = single
    ? (cache?.CommonData.Product.find((p) => p.ProductID === single.productId)?.Name
        ?? single.productName
        ?? `Product #${single.productId}`)
    : null;

  // What the single-product edit touches, for the card label and the button text.
  const fieldLabels = single
    ? [
        single.newName != null ? "name" : null,
        single.price != null ? "price" : null,
        single.tax != null ? "tax" : null,
        single.externalItemId != null ? "external item ID" : null,
        single.externalCost != null ? "external cost" : null,
      ].filter((f): f is string => f !== null)
    : [];

  const title = single
    ? `${productName} — ${fieldLabels.join(", ")}`
    : `${changes.length} product prices`;

  return (
    <div className={styles.dealCard}>
      <div className={styles.dealCardHeader}>
        <span className={styles.dealCardBadge}>{single ? "Product edit" : "Price change"}</span>
        <span className={styles.dealCardTitle}>{title}</span>
      </div>
      {description && <p className={styles.dealCardDescription}>{description}</p>}

      {recorded && (
        <div className={styles.dealCardDone}>
          <span className={styles.dealCardDoneMark} aria-hidden="true">✓</span>
          Applied to {recorded.count ?? 1} {(recorded.count ?? 1) === 1 ? "product" : "products"}
          {formatRecordedAt(recorded.at) && (
            <span className={styles.dealCardDoneTime}> · {formatRecordedAt(recorded.at)}</span>
          )}
        </div>
      )}

      <div className={styles.chatActionRow}>
        <Button variant="primary" size="sm" onClick={() => setOpen(true)}>
          {recorded ? "Review" : single ? "Edit product" : "Review changes"}
        </Button>
      </div>

      {open && (
        <Suspense fallback={null}>
          {single ? (
            // One product → the full product editor, with Issa's values filled in.
            <ProductPopup
              editId={String(single.productId)}
              prefill={{
                prices: { 1: single.price, 2: single.tax },
                ...(single.newName != null ? { name: single.newName } : {}),
                ...(single.externalItemId != null ? { externalItemId: single.externalItemId } : {}),
                ...(single.externalCost != null ? { externalCost: single.externalCost } : {}),
              }}
              onClose={() => setOpen(false)}
              onSaved={() => setRecorded(recordAction(key, single.productId, "prices", 1))}
            />
          ) : (
            // Several products → a before/after review list with an override warning.
            <ProductPriceChangeModal
              changes={changes}
              onClose={() => setOpen(false)}
              onApplied={(count) => setRecorded(recordAction(key, 0, "prices", count))}
            />
          )}
        </Suspense>
      )}
    </div>
  );
}

/** What the editor is currently opened for, if anything. */
type EditorTarget = {
  dealId: number;
  deal?: PackageDeal;
  highlight?: string[];
  /** A generated image from this answer, attached as the deal's kiosk image. */
  imageDataUri?: string;
} | null;

function PackageDealActionCard({ action, messageId, index, images }: {
  action: IssaChatActionRaw;
  messageId?: number;
  index: number;
  images?: string[];
}) {
  const { cache, canWrite } = useAuth();
  const assistant = useAssistant();
  const mapped = useMemo(() => mapCreatePackageDeal(action.body), [action.body]);
  // Until the DB's kiosks read free mix, Issa's free-mix groups go to the editor as N
  // identical groups of one (expandFreeMixGroups). Only the groups Issa supplied: a stored
  // deal's own groups are left exactly as they are.
  const freeMixEnabled = useMemo(() => getDbFeatureFlags(cache).PackageDealFreeMix === true, [cache]);
  const key = useMemo(() => actionKey(messageId, index, action), [messageId, index, action]);

  const [recorded, setRecorded] = useState<RecordedDealAction | null>(() => getRecordedAction(key));
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [editor, setEditor] = useState<EditorTarget>(null);

  const pageRowId = useMemo(
    () => cache?.PageInfo.Page.find((p) => p.Name?.toLowerCase() === "packagedeal")?.RowID,
    [cache],
  );
  if (pageRowId == null || !canWrite(pageRowId)) return null;

  if (!mapped.ok) {
    return (
      <div className={styles.dealCard}>
        <span className={styles.chatActionError}>
          {assistant.name} suggested a package deal, but it couldn't be read: {mapped.error}. Ask {assistant.name} to suggest it again.
        </span>
      </div>
    );
  }
  const { deal, patch, dealId, description } = mapped;
  // The picture Issa asked to attach, resolved against this answer's images.
  // Only for the first create/edit: once acted on, the image is in the deal.
  const attachedImage =
    mapped.imageIndex !== null ? images?.[mapped.imageIndex - 1] : undefined;
  // Editing when Issa targeted an existing deal, or when this card already
  // created one (the deal exists now, so the action becomes an edit of it).
  const targetDealId = recorded?.dealId ?? dealId;
  const isEdit = targetDealId > 0;
  // Card label only — the deal is a "package" when the cart needs two or more items for
  // it: two groups ("one from each"), or one group of 2+ ("any 2 candies"). A single item
  // is a plain discount, so calling it a package deal there just reads wrong. Nothing
  // about the saved deal changes.
  const itemsNeeded = (deal.groups ?? []).reduce((sum, g) => sum + Math.max(1, g.i ?? 1), 0);
  const dealKindLabel = itemsNeeded > 1 ? "Package deal" : "Deal";
  // Create cards only: on an edit Issa may omit a group's count to keep the stored one,
  // which this card cannot see until the deal is fetched.
  const groupSummary = dealId > 0 ? null : describeDealGroups(deal.groups);

  /**
   * Open the editor. Three cases:
   *  - already acted on → open the stored deal as-is (Issa's changes are in it)
   *  - Issa targeted a deal → fetch it and overlay only the fields Issa supplied
   *  - new deal → open create mode prefilled with Issa's suggestion
   */
  const handleOpen = async () => {
    setError(null);
    if (recorded) {
      setEditor({ dealId: recorded.dealId });
      return;
    }
    if (!isEdit) {
      const prefill = freeMixEnabled ? deal : { ...deal, groups: expandFreeMixGroups(deal.groups) };
      setEditor({ dealId: 0, deal: prefill, imageDataUri: attachedImage });
      return;
    }
    setLoading(true);
    try {
      const r = await getPackageDeal(dealId);
      if (r.bError || !r.oData) throw new Error(r.sMessage || `Could not load deal #${dealId}. Try again in a moment.`);
      // Expanded after the merge, so a count Issa left to the stored group is known.
      const merged = applyDealPatch(r.oData, patch);
      // Highlight what Issa changed, so the operator can see the edit against the stored deal.
      setEditor({
        dealId,
        deal: freeMixEnabled || !patch.fields.groups
          ? merged
          : { ...merged, groups: expandFreeMixGroups(merged.groups) },
        highlight: patchHighlightFields(patch),
        imageDataUri: attachedImage,
      });
    } catch (e) {
      setError(e instanceof Error ? e.message : `Could not load deal #${dealId}. Try again in a moment.`);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className={styles.dealCard}>
      <div className={styles.dealCardHeader}>
        <span className={styles.dealCardBadge}>
          {isEdit ? `Deal #${targetDealId}` : dealKindLabel}
        </span>
        <span className={styles.dealCardTitle}>
          {deal.title || `Deal #${targetDealId}`}
          {groupSummary && ` — ${groupSummary}`}
        </span>
      </div>
      {description && <p className={styles.dealCardDescription}>{description}</p>}

      {recorded && (
        <div className={styles.dealCardDone}>
          <span className={styles.dealCardDoneMark} aria-hidden="true">✓</span>
          {recorded.kind === "update" ? "Updated" : "Created"} deal #{recorded.dealId}
          {formatRecordedAt(recorded.at) && (
            <span className={styles.dealCardDoneTime}> · {formatRecordedAt(recorded.at)}</span>
          )}
        </div>
      )}

      <div className={styles.chatActionRow}>
        <Button variant="primary" size="sm" loading={loading} onClick={handleOpen}>
          {isEdit ? "Edit" : "Create"}
        </Button>
      </div>

      {error && <span className={styles.chatActionError} role="alert">{error}</span>}

      {editor && (
        <Suspense fallback={null}>
          <PackageDealEditor
            mode={PackageDealMode.Standard}
            dealId={editor.dealId}
            initialDeal={editor.deal}
            highlightFields={editor.highlight}
            initialImageDataUri={editor.imageDataUri}
            onClose={() => setEditor(null)}
            onSaved={(savedDealId) => {
              // Remember what this card did, so re-opening the conversation shows
              // the outcome instead of offering to create a duplicate.
              const id = savedDealId ?? editor.dealId;
              if (id && id > 0) {
                setRecorded(recordAction(key, id, editor.dealId > 0 ? "update" : "create"));
              }
            }}
          />
        </Suspense>
      )}
    </div>
  );
}

function formatRecordedAt(iso: string): string {
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? "" : d.toLocaleString();
}
